import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  LoaderCircleIcon,
  PrinterIcon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'
import { PrintPortal, usePrintJob } from '@app/lib/print-portal'
import { cariApi, cariQueryKeys } from '@app/features/cari/api'
import type {
  CariBalance,
  CariDetailResponse,
  CariLocationBalance,
  CariStatementRow,
} from '@app/features/cari/api'
import {
  formatAmount,
  formatDate,
  formatDateTime,
  formatMoney,
  formatQty,
  percentText,
  todayIstanbul,
} from '@app/features/cari/format'
import {
  BalanceAmount,
  CariTypeBadge,
  EmptyState,
  ErrorState,
  TableSkeleton,
  balanceToneClass,
} from '@app/features/cari/ui'

/**
 * Cari detail — the account card, its position per currency, and its ekstre.
 *
 * The statement is scoped to ONE currency at a time (tabs above the table).
 * That is not a simplification: Korgun never converts between currencies, so a
 * running balance over mixed TL and USD rows would be a meaningless number.
 * The running balance itself is computed server-side over the whole statement,
 * so it stays correct even though the table is paged newest-first.
 */

const STATEMENT_PAGE_SIZE = 50

/**
 * Matches the Worker's `MAX_PAGE_SIZE` clamp in `worker/api/cari.ts` — the
 * biggest page the endpoint will hand back, so the print fetch loop below
 * needs the fewest possible round trips.
 */
const PRINT_PAGE_SIZE = 200

export function CariDetailScreen() {
  const { ckod } = useParams({ from: '/_korumali/cari/$ckod' })
  const [currency, setCurrency] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const params = { currency, page, pageSize: STATEMENT_PAGE_SIZE }

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: cariQueryKeys.detail(ckod, params),
    queryFn: () => cariApi.detail(ckod, params),
    placeholderData: keepPreviousData,
  })

  function selectCurrency(next: string) {
    setCurrency(next)
    setPage(1)
  }

  // 404 is the server's final answer — retrying cannot conjure the account up,
  // so the only useful action is the way back to the list.
  const notFound = error instanceof ApiError && error.status === 404

  return (
    <>
      <div className="mb-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/cari">
            <ArrowLeftIcon aria-hidden />
            Cari
          </Link>
        </Button>
      </div>

      {isPending ? (
        <DetailSkeleton />
      ) : isError ? (
        <Card>
          {notFound ? (
            <ErrorState
              title="Cari kartı bulunamadı"
              message={`${ckod} kodlu cari Korgun'da kayıtlı değil. Listeden doğru kartı seçebilirsiniz.`}
              action={
                <Button asChild variant="primary">
                  <Link to="/cari">Cari listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              message={error instanceof Error ? error.message : 'Cari kartı getirilemedi.'}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      ) : (
        <DetailContent
          ckod={ckod}
          data={data}
          busy={isFetching}
          page={page}
          onPage={setPage}
          onCurrency={selectCurrency}
        />
      )}
    </>
  )
}

function DetailContent({
  ckod,
  data,
  busy,
  page,
  onPage,
  onCurrency,
}: {
  /**
   * The ROUTE's own param, straight from `useParams` — not `data.header.ckod`.
   * `data` is served through `keepPreviousData`, so during a `/cari/A` →
   * `/cari/B` navigation `header.ckod` keeps reading "A" until B's query
   * resolves. `useStatementPrint` needs the account identity that changes the
   * instant the route does, or a print fetch that finishes in that window
   * would still open A's statement on B's page.
   */
  ckod: string
  data: CariDetailResponse
  busy: boolean
  page: number
  onPage: (page: number) => void
  onCurrency: (currency: string) => void
}) {
  const { header, balances, locationBalances, statement, openOrders, recentInvoices } = data
  /*
   * `data` (and everything destructured from it above, including `header` and
   * `statement.currency`) is served through `keepPreviousData`: during a
   * `/cari/A` -> `/cari/B` navigation it keeps reading A's response until B's
   * query resolves, even though `ckod` (the route) has already moved to B. A
   * print started in that window would fetch B's statement in A's currency
   * (or whatever currency A happened to have) and label it with A's name —
   * two different accounts stapled into one "financial statement". Printing
   * only proceeds once the two agree again.
   */
  const identityMatchesRoute = header.ckod === ckod
  const print = useStatementPrint(ckod, statement.currency, identityMatchesRoute)

  return (
    <div className="flex flex-col gap-3">
      {/* ── Header card ──────────────────────────────────────────────────── */}
      <Card>
        <div className="flex flex-col gap-4 px-3.5 py-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="eyebrow">
              Cari <span className="font-mono">{header.ckod}</span>
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-semibold tracking-tight text-ink">
              {header.name}
            </h2>
            {header.title && header.title !== header.name ? (
              <p className="mt-0.5 text-data text-ink-muted">{header.title}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <CariTypeBadge type={header.type} typeName={header.typeName} />
              {header.inactive ? <Badge tone="neutral">Pasif kart</Badge> : null}
              {header.overseas ? <Badge tone="info">Yurt dışı</Badge> : null}
              {balances.length > 1 ? (
                <Badge tone="warn" title="Bu carinin birden fazla para biriminde hareketi var.">
                  {balances.length} para birimi
                </Badge>
              ) : null}
            </div>
          </div>

          <BalanceCards balances={balances} />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-hairline px-3.5 py-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="Vergi dairesi" value={header.taxOffice ?? '—'} />
          <Field label="Vergi no" value={header.taxNumber ?? header.nationalId ?? '—'} mono />
          <Field label="Şehir" value={header.city ?? '—'} />
          <Field label="İlçe" value={header.district ?? '—'} />
          <Field label="Ülke" value={header.country ?? '—'} />
          <Field label="Telefon" value={header.phone ?? header.phone2 ?? '—'} mono />
          <Field
            label="E-posta"
            value={
              header.email ? (
                <a className="hover:text-kordovan" href={`mailto:${header.email}`}>
                  {header.email}
                </a>
              ) : (
                '—'
              )
            }
          />
          <Field label="Varsayılan para birimi" value={header.defaultCurrency ?? '—'} mono />
          <Field label="Son hareket" value={formatDate(data.lastActivity)} mono />
          <Field label="Son kayıt" value={formatDateTime(header.lastPostedAt)} mono />
          <Field label="Kart açılışı" value={formatDateTime(header.createdAt)} mono />
          <Field label="Son güncelleme" value={formatDateTime(header.updatedAt)} mono />
        </dl>

        {header.address || header.notes ? (
          <div className="border-t border-hairline px-3.5 py-3">
            {header.address ? (
              <>
                <p className="eyebrow">Adres</p>
                <p className="mt-1 text-data text-ink">
                  {header.address}
                  {header.postCode ? ` · ${header.postCode}` : ''}
                </p>
              </>
            ) : null}
            {header.notes ? (
              <>
                <p className={cn('eyebrow', header.address && 'mt-3')}>Not</p>
                <p className="mt-1 whitespace-pre-line text-data text-ink">{header.notes}</p>
              </>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* ── Balances per location ────────────────────────────────────────── */}
      {locationBalances.length > 1 ? (
        <LocationBalanceCard rows={locationBalances} />
      ) : null}

      {/* ── Statement ────────────────────────────────────────────────────── */}
      <StatementCard
        statement={statement}
        busy={busy}
        page={page}
        onPage={onPage}
        onCurrency={onCurrency}
        onPrint={print.run}
        printing={print.status === 'loading'}
        printError={print.status === 'error'}
        printDisabled={!identityMatchesRoute}
      />

      {/* ── Open orders ──────────────────────────────────────────────────── */}
      <OpenOrdersCard rows={openOrders} total={data.openOrderCount} />

      {/* ── Recent invoices ──────────────────────────────────────────────── */}
      <RecentInvoicesCard rows={recentInvoices} />

      {/* Portalled straight onto <body> — see `PrintStatement` and the print
          rules in styles.css for why. Invisible on screen either way. `currency`
          comes from the print state itself (the currency that was actually
          fetched), NOT `statement.currency` — that can move on while the print
          fetch is still in flight (currency tab switch, `keepPreviousData`
          settling), which would otherwise mislabel a finished fetch with
          whatever currency the screen happens to show by the time it lands. */}
      {print.status === 'ready' ? (
        <PrintStatement
          header={header}
          currency={print.data.currency}
          rows={print.data.rows}
          totals={print.data.totals}
        />
      ) : null}
    </div>
  )
}

/* ── Print ekstre ─────────────────────────────────────────────────────────── */

/**
 * Toggled on <body> for the moment `window.print()` is open so the print
 * stylesheet (styles.css) knows to hide the app shell. Scoped to this class
 * rather than firing on every print — a bare `#root { display: none }` under
 * `@media print` would blank out a native Ctrl+P on any OTHER route (nothing
 * there ever renders `.print-only`), and even on the cari detail page itself
 * before "Yazdır" has ever been clicked.
 */
const PRINT_BODY_CLASS = 'print-statement'

interface PrintTotals {
  totalDebit: number
  totalCredit: number
  closingBalance: number
}

/**
 * What one "Yazdır" run produces: the whole statement in ONE currency, plus
 * that currency — carried in the snapshot rather than read from the screen at
 * render time, because the currency tab (or a `keepPreviousData` settle) can
 * move on while the fetch is in flight, which would label a finished fetch
 * with a currency it never asked for.
 */
interface StatementSnapshot {
  currency: string
  rows: CariStatementRow[]
  totals: PrintTotals
}

/**
 * Drives "Yazdır": fetches every page of the active-currency statement (not
 * just the one on screen), then opens the browser print dialog once the
 * print-only table has painted. Everything about the print lifecycle itself —
 * body class, `afterprint` reset, supersession, abort — lives in `usePrintJob`
 * (lib/print-portal.tsx); this hook only says WHAT to fetch.
 *
 * `identityMatchesRoute` is false for the brief `keepPreviousData` window
 * where the caller's `ckod`/`currency` already reflect the new route but the
 * `header` that will end up in `PrintStatement` still belongs to the account
 * being navigated away from — see the caller for the full explanation. `run`
 * refuses to start until they agree again, the one guard that also rules out
 * the derived case of a currency that belongs to the wrong account.
 */
function useStatementPrint(ckod: string, currency: string, identityMatchesRoute: boolean) {
  return usePrintJob<StatementSnapshot>({
    bodyClass: PRINT_BODY_CLASS,
    resetKey: ckod,
    enabled: identityMatchesRoute,
    // An account whose ledger came back empty has nothing to print; the button
    // is already hidden in that case, so this is the belt to that braces.
    canPrint: (snapshot) => snapshot.rows.length > 0,
    load: async (signal) => {
      const { rows, totals } = await fetchFullStatement(ckod, currency, signal)
      return { currency, rows, totals }
    },
  })
}

/**
 * Walks every page of the statement endpoint at the largest page size the
 * Worker allows, sequentially — `totalPages` is only known after the first
 * response, and this report is printed rarely enough that firing dozens of
 * requests in parallel against a long ledger would be needless load.
 *
 * Each page is its own offset-paginated snapshot, not one server-side cursor
 * over a frozen view — a posting landing mid-loop could in theory shift a
 * later page's boundary. Accepted as-is: the ledger only ever moves via the
 * one-way Korgun→mirror sync (batch, not live), not from anything a user does
 * in this read-only app, so the odds of a write landing in the ~1 round trip
 * this loop takes are effectively nil. A true snapshot would need a
 * server-side export endpoint, out of scope for a UI-only change.
 *
 * What the loop does NOT assume is that it got the page it asked for. The
 * endpoint answers with the currency and page it actually served, and both can
 * differ from the request: a currency the account has no movements in falls
 * back to its busiest one, and a page past the end falls back to page 1 (see
 * `worker/api/cari.ts`). Trusting the request would then either mix two
 * currencies into one "statement" or append page 1 to itself — a printed
 * financial document that is quietly wrong. Every response is checked instead,
 * and a mismatch aborts the whole print rather than printing the damage.
 */
async function fetchFullStatement(
  ckod: string,
  currency: string,
  signal: AbortSignal,
): Promise<{ rows: CariStatementRow[]; totals: PrintTotals }> {
  const rows: CariStatementRow[] = []
  let totals: PrintTotals = { totalDebit: 0, totalCredit: 0, closingBalance: 0 }
  let page = 1

  for (;;) {
    const res = await cariApi.detail(
      ckod,
      { currency, page, pageSize: PRINT_PAGE_SIZE },
      { signal },
    )
    // Diagnostic only — never rendered. The user sees the print error state's
    // own Turkish message; this text is for the console.
    if (res.statement.currency !== currency || res.statement.page !== page) {
      throw new Error(
        `Statement page drifted: got ${res.statement.currency}/${res.statement.page}, asked for ${currency}/${page}`,
      )
    }

    rows.push(...res.statement.rows)
    totals = {
      totalDebit: res.statement.totalDebit,
      totalCredit: res.statement.totalCredit,
      closingBalance: res.statement.closingBalance,
    }
    if (page >= res.statement.totalPages || res.statement.rows.length === 0) break
    // A newer print, an account change or an unmount can land between two
    // pages — stop here instead of spending a round trip on a result that is
    // already unwanted.
    signal.throwIfAborted()
    page += 1
  }

  return { rows, totals }
}

/**
 * `rows` arrives sorted `dt desc nulls last` (the Worker's page order — see
 * `worker/api/cari.ts`), one page newest-first. A plain `.reverse()` would
 * turn that into `dt asc nulls FIRST` — every undated row (no due date on the
 * document) would jump to the very front of the printed statement, still
 * carrying the running balance it was given for sorting LAST (`nulls last`
 * holds in the server's own ascending running-balance scan too). Splitting
 * the null-dated rows out, reversing only the dated ones, and appending the
 * nulls back at the end keeps both sub-orders consistent with how the
 * running balance was actually computed.
 */
function chronologicalOrder(rows: readonly CariStatementRow[]): CariStatementRow[] {
  const reversed = [...rows].reverse()
  const dated = reversed.filter((row) => row.date !== null)
  const undated = reversed.filter((row) => row.date === null)
  return [...dated, ...undated]
}

/** Earliest and latest document date across the fetched rows, for the letterhead. */
function dateSpan(rows: readonly CariStatementRow[]): { from: string | null; to: string | null } {
  let from: string | null = null
  let to: string | null = null
  for (const row of rows) {
    if (!row.date) continue
    if (from === null || row.date < from) from = row.date
    if (to === null || row.date > to) to = row.date
  }
  return { from, to }
}

/**
 * The printable ekstre itself — rendered through `PrintPortal` straight onto
 * `document.body`, a SIBLING of `#root`, not a descendant of it. That is what
 * lets the print stylesheet hide the whole app shell for `body.print-statement`
 * (see styles.css) without ever touching the shell component, and without
 * leaving behind blank pages from invisible screen layout.
 *
 * Only ever rendered from the print job's `'ready'` branch, so `rows` and
 * `totals` are always the matched, complete pair for one fetch.
 *
 * Hidden on screen (`.print-only` is `display: none` outside `@media print`),
 * so it never affects the normal layout.
 */
function PrintStatement({
  header,
  currency,
  rows,
  totals,
}: {
  header: CariDetailResponse['header']
  currency: string
  rows: CariStatementRow[]
  totals: PrintTotals
}) {
  // Read top-to-bottom like a paper ledger: oldest first, running balance
  // building up to the closing balance on the last row.
  const chronological = chronologicalOrder(rows)
  const { from, to } = dateSpan(rows)

  return (
    <PrintPortal className="print-only">
      <header>
        <div>
          <p className="print-eyebrow">Panel — Cari ekstre</p>
          <h1>{header.name}</h1>
          <p>
            <span className="font-mono">{header.ckod}</span> · {currency}
            {header.title && header.title !== header.name ? ` · ${header.title}` : ''}
          </p>
        </div>
        <div className="print-meta">
          <p>
            {from ? formatDate(from) : '—'} – {to ? formatDate(to) : '—'}
          </p>
          <p className="print-balance">
            Kapanış bakiyesi <strong>{formatAmount(totals.closingBalance, currency)}</strong>
          </p>
        </div>
      </header>

      <table>
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Belge</th>
            <th>Açıklama</th>
            <th className="num">Borç</th>
            <th className="num">Alacak</th>
            <th className="num">Bakiye</th>
          </tr>
        </thead>
        <tbody>
          {chronological.map((row) => (
            <tr key={row.id}>
              <td>{formatDate(row.date)}</td>
              <td>
                {row.docTypeName}
                {row.docNo ? ` · ${row.docNo}` : ''}
              </td>
              <td>{row.description ?? '—'}</td>
              <td className="num">{formatMoney(row.debit)}</td>
              <td className="num">{formatMoney(row.credit)}</td>
              {/* `formatMoney` reads a zero amount as "nothing to show" (em dash) —
                  right for debit/credit, wrong for a running balance, where an
                  account settled to exactly zero is a real, meaningful state. Same
                  guard as the on-screen table. */}
              <td className="num">
                {formatMoney(row.runningBalance) === '—' ? '0,00' : formatMoney(row.runningBalance)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Toplam</td>
            <td className="num">{formatMoney(totals.totalDebit)}</td>
            <td className="num">{formatMoney(totals.totalCredit)}</td>
            <td className="num">{formatAmount(totals.closingBalance, currency)}</td>
          </tr>
        </tfoot>
      </table>

      <footer>Panel — {formatDate(todayIstanbul())}</footer>
    </PrintPortal>
  )
}

/* ── Balance cards ────────────────────────────────────────────────────────── */

function BalanceCards({ balances }: { balances: readonly CariBalance[] }) {
  if (balances.length === 0) {
    return (
      <div className="shrink-0 rounded-sm border border-hairline bg-kagit px-3 py-2.5 lg:w-72">
        <p className="eyebrow">Bakiye</p>
        <p className="mt-1 text-data text-ink-muted">Bu kartta hiç hareket yok.</p>
      </div>
    )
  }

  return (
    <div className="flex shrink-0 flex-col gap-2 lg:w-72">
      {balances.map((b) => (
        <div key={b.currency} className="rounded-sm border border-hairline bg-kagit px-3 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="eyebrow">{b.currency} bakiye</span>
            <span className={cn('font-mono text-xl tnum font-semibold', balanceToneClass(b.balance))}>
              {formatAmount(b.balance, b.currency)}
            </span>
          </div>
          <p className="mt-1 text-right text-micro text-ink-muted">
            {b.balance > 0
              ? 'cari bize borçlu'
              : b.balance < 0
                ? 'biz cariye borçluyuz'
                : 'kapalı hesap'}
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-2 border-t border-hairline pt-2 text-center">
            <MiniStat label="Borç" value={formatMoney(b.debit)} />
            <MiniStat label="Alacak" value={formatMoney(b.credit)} />
          </dl>
        </div>
      ))}
    </div>
  )
}

function LocationBalanceCard({ rows }: { rows: readonly CariLocationBalance[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Depo / şube kırılımı</CardTitle>
      </CardHeader>
      <TableScroller>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-44">Lokasyon</TableHead>
              <TableHead className="w-20">Para birimi</TableHead>
              <TableHead className="w-32 text-right">Borç</TableHead>
              <TableHead className="w-32 text-right">Alacak</TableHead>
              <TableHead className="w-36 text-right">Bakiye</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.location}|${r.currency}`}>
                <TableCell>
                  <span className="font-medium text-ink">{r.locationName ?? r.location}</span>
                  <span className="ml-1.5 font-mono text-micro text-ink-muted">{r.location}</span>
                </TableCell>
                <TableCell className="font-mono text-ink-muted">{r.currency}</TableCell>
                <TableNumCell className="text-ink-muted">{formatMoney(r.debit)}</TableNumCell>
                <TableNumCell className="text-ink-muted">{formatMoney(r.credit)}</TableNumCell>
                <TableNumCell>
                  <BalanceAmount balance={r.balance} currency={r.currency} />
                </TableNumCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScroller>
    </Card>
  )
}

/* ── Statement (ekstre) ───────────────────────────────────────────────────── */

function StatementCard({
  statement,
  busy,
  page,
  onPage,
  onCurrency,
  onPrint,
  printing,
  printError,
  printDisabled,
}: {
  statement: CariDetailResponse['statement']
  busy: boolean
  page: number
  onPage: (page: number) => void
  onCurrency: (currency: string) => void
  onPrint: () => void
  printing: boolean
  printError: boolean
  /** True for the brief window after navigating to a different account while
   * the previous one's data is still on screen (`keepPreviousData`) — printing
   * then would risk stapling two accounts' data into one statement. */
  printDisabled: boolean
}) {
  const { rows, currencies, currency, total, totalPages } = statement
  const serverPage = statement.page
  const first = total === 0 ? 0 : (serverPage - 1) * statement.pageSize + 1
  const last = Math.min(serverPage * statement.pageSize, total)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ekstre</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {currencies.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1">
              {currencies.map((code) => {
                const active = code === currency
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => onCurrency(code)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-xs border px-2 py-1 font-mono text-micro transition-colors',
                      active
                        ? 'border-kordovan/30 bg-kordovan/8 font-medium text-kordovan'
                        : 'border-hairline bg-surface text-ink-muted hover:border-ink-muted/40 hover:text-ink',
                    )}
                  >
                    {code}
                  </button>
                )
              })}
            </div>
          ) : null}
          {rows.length > 0 ? (
            <Button variant="outline" size="sm" onClick={onPrint} disabled={printing || printDisabled}>
              {printing ? (
                <LoaderCircleIcon className="animate-spin" aria-hidden />
              ) : (
                <PrinterIcon aria-hidden />
              )}
              Yazdır
            </Button>
          ) : null}
        </div>
      </CardHeader>
      {printError ? (
        <p className="border-b border-hairline px-3.5 py-2 text-micro text-kordovan">
          Ekstrenin tamamı alınamadı, yazdırma başlatılamadı. Tekrar deneyin.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          title="Bu carinin ekstresi boş"
          description="Korgun'da bu hesaba hiç fiş, fatura ya da çek işlenmemiş. Cari listesine dönüp hareketi olan bir kart açabilirsiniz."
          action={
            <Button asChild variant="primary">
              <Link to="/cari">Cari listesine dön</Link>
            </Button>
          }
        />
      ) : (
        <>
          <TableScroller className={cn(busy && 'opacity-60 transition-opacity')}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Tarih</TableHead>
                  <TableHead className="w-40">Belge</TableHead>
                  <TableHead className="min-w-52">Açıklama</TableHead>
                  <TableHead className="hidden w-28 lg:table-cell">Lokasyon</TableHead>
                  <TableHead className="w-28 text-right">Borç</TableHead>
                  <TableHead className="w-28 text-right">Alacak</TableHead>
                  <TableHead className="w-32 text-right">Bakiye</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono tnum text-ink-muted">
                      {formatDate(row.date)}
                    </TableCell>

                    <TableCell>
                      <span className="block truncate text-data text-ink">{row.docTypeName}</span>
                      {row.docNo ? (
                        <span className="block truncate font-mono text-micro text-ink-muted">
                          <DocNoLink docNo={row.docNo} link={row.link} />
                        </span>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      <span className="block truncate text-ink-muted" title={row.description ?? undefined}>
                        {row.description ?? '—'}
                      </span>
                      {row.dueDate ? (
                        <span className="block font-mono text-micro text-ink-muted/70">
                          vade {formatDate(row.dueDate)}
                        </span>
                      ) : null}
                    </TableCell>

                    <TableCell className="hidden truncate text-micro text-ink-muted lg:table-cell">
                      {row.locationName ?? row.location ?? '—'}
                    </TableCell>

                    <TableNumCell className={cn(row.debit > 0 ? 'text-ink' : 'text-ink-muted/60')}>
                      {formatMoney(row.debit)}
                    </TableNumCell>

                    <TableNumCell className={cn(row.credit > 0 ? 'text-ink' : 'text-ink-muted/60')}>
                      {formatMoney(row.credit)}
                    </TableNumCell>

                    <TableNumCell className={cn('font-medium', balanceToneClass(row.runningBalance))}>
                      {formatMoney(row.runningBalance) === '—' ? '0,00' : formatMoney(row.runningBalance)}
                    </TableNumCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScroller>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-3.5 py-2.5">
            <span className="font-mono text-micro tnum text-ink-muted">
              {formatQty(first)}–{formatQty(last)} / {formatQty(total)} hareket
            </span>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-micro text-ink-muted">
                Borç <span className="font-mono tnum text-ink">{formatMoney(statement.totalDebit)}</span>
              </span>
              <span className="text-micro text-ink-muted">
                Alacak{' '}
                <span className="font-mono tnum text-ink">{formatMoney(statement.totalCredit)}</span>
              </span>
              <span className="text-micro text-ink-muted">
                Bakiye{' '}
                <span
                  className={cn(
                    'font-mono text-data tnum font-semibold',
                    balanceToneClass(statement.closingBalance),
                  )}
                >
                  {formatAmount(statement.closingBalance, currency)}
                </span>
              </span>
            </div>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-end gap-1.5 border-t border-hairline px-3.5 py-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPage(Math.max(1, page - 1))}
              >
                <ChevronLeftIcon aria-hidden />
                Önceki
              </Button>
              <span className="px-1 font-mono text-micro tnum text-ink-muted">
                {formatQty(serverPage)} / {formatQty(totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => onPage(Math.min(totalPages, page + 1))}
              >
                Sonraki
                <ChevronRightIcon aria-hidden />
              </Button>
            </div>
          ) : null}

          <p className="border-t border-hairline px-3.5 py-2 text-micro text-ink-muted">
            Bakiye sütunu en eskiden en yeniye doğru işletilir; tablo en yeni hareket üstte
            sıralandığı için sayfa değişse de her satır o belgeden sonraki gerçek bakiyeyi gösterir.
            Ekstre tek para biriminde tutulur — Korgun para birimleri arasında çevrim yapmaz.
          </p>
        </>
      )}
    </Card>
  )
}

/**
 * Document number, linked when it resolves to a record this app can open.
 *
 * Only the production arm ever fires today: the piece-work payroll vouchers
 * carry `Urtx_Emir.EmirNo` in their `BelgeNo`, while nothing in the cari ledger
 * references a sales order (see the note in `worker/api/cari.ts`). The 'order'
 * arm is here so the day such a link exists, only this function changes.
 */
function DocNoLink({
  docNo,
  link,
}: {
  docNo: string
  link: CariDetailResponse['statement']['rows'][number]['link']
}) {
  if (link === null) return <>{docNo}</>

  if (link.kind === 'production') {
    return (
      <Link
        to="/uretim/$emirNo"
        params={{ emirNo: String(link.emirNo) }}
        className="inline-flex items-center gap-1 hover:text-kordovan"
        title="Bu hakediş fişinin üretim emrini aç"
      >
        {docNo}
        <ExternalLinkIcon className="size-3" aria-hidden />
      </Link>
    )
  }

  return (
    <Link
      to="/siparisler/$sipNo"
      params={{ sipNo: String(link.sipNo) }}
      className="inline-flex items-center gap-1 hover:text-kordovan"
      title="Bu belgenin siparişini aç"
    >
      {docNo}
      <ExternalLinkIcon className="size-3" aria-hidden />
    </Link>
  )
}

/* ── Open orders ──────────────────────────────────────────────────────────── */

function OpenOrdersCard({
  rows,
  total,
}: {
  rows: CariDetailResponse['openOrders']
  total: number
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Açık siparişler</CardTitle>
        {total > rows.length ? (
          <span className="font-mono text-micro tnum text-ink-muted">
            ilk {formatQty(rows.length)} / {formatQty(total)}
          </span>
        ) : null}
      </CardHeader>

      {rows.length === 0 ? (
        <EmptyState
          title="Açık sipariş yok"
          description="Bu carinin sevk bekleyen siparişi yok. Sipariş defterinin tamamına siparişler ekranından bakabilirsiniz."
          action={
            <Button asChild variant="outline">
              <Link to="/siparisler">Siparişlere git</Link>
            </Button>
          }
        />
      ) : (
        <TableScroller>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Sipariş</TableHead>
                <TableHead className="hidden w-24 md:table-cell">Tarih</TableHead>
                <TableHead className="w-24">Tip</TableHead>
                <TableHead className="w-32">Termin</TableHead>
                <TableHead className="w-20 text-right">Adet</TableHead>
                <TableHead className="hidden w-20 text-right lg:table-cell">Sevk</TableHead>
                <TableHead className="w-20 text-right">Kalan</TableHead>
                <TableHead className="w-16 text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.sipNo} className="relative">
                  <TableCell className="font-mono">
                    {/*
                      A supplier's open "orders" are purchase orders (SipTip
                      'A'), whose canonical screen is /satinalma — the sales
                      view would read the INVERTED ledger the wrong way round.
                      /siparisler/<n> redirects there anyway; linking straight
                      at it skips the bounce.
                    */}
                    {row.type === 'A' ? (
                      <Link
                        to="/satinalma/$sipNo"
                        params={{ sipNo: String(row.sipNo) }}
                        title={`${row.sipNo} numaralı alış siparişini aç`}
                        className="font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                      >
                        {row.sipNo}
                      </Link>
                    ) : (
                      <Link
                        to="/siparisler/$sipNo"
                        params={{ sipNo: String(row.sipNo) }}
                        title={`${row.sipNo} numaralı siparişi aç`}
                        className="font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                      >
                        {row.sipNo}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell className="hidden font-mono tnum text-ink-muted md:table-cell">
                    {formatDate(row.orderDate)}
                  </TableCell>
                  <TableCell>
                    <Badge tone={row.type === 'A' ? 'info' : 'neutral'}>{row.typeName}</Badge>
                  </TableCell>
                  <TableCell>
                    {row.overdueDays === null ? (
                      <span className="font-mono tnum text-ink-muted">
                        {formatDate(row.deliveryDate)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <span className="font-mono tnum font-medium text-hardal">
                          {formatDate(row.deliveryDate)}
                        </span>
                        <span className="rounded-xs bg-hardal/10 px-1 font-mono text-micro tnum text-hardal">
                          +{formatQty(row.overdueDays)} g
                        </span>
                      </span>
                    )}
                  </TableCell>
                  <TableNumCell>{formatQty(row.orderedQty)}</TableNumCell>
                  <TableNumCell className="hidden text-ink-muted lg:table-cell">
                    {formatQty(row.shippedQty)}
                  </TableNumCell>
                  <TableNumCell className="font-medium text-ink">
                    {formatQty(row.kalanQty)}
                  </TableNumCell>
                  <TableNumCell className="text-ink-muted">{percentText(row.percent)}</TableNumCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

/* ── Recent invoices ──────────────────────────────────────────────────────── */

function RecentInvoicesCard({ rows }: { rows: CariDetailResponse['recentInvoices'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Son faturalar</CardTitle>
        <span className="text-micro text-ink-muted">en yeni {rows.length} kayıt</span>
      </CardHeader>

      {rows.length === 0 ? (
        <EmptyState
          title="Fatura yok"
          description="Bu cariye kesilmiş ya da bu cariden gelen bir fatura Korgun'da bulunmuyor. Faturalama Korgun'da kalmaya devam ediyor."
        />
      ) : (
        <TableScroller>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Tarih</TableHead>
                <TableHead className="min-w-36">Fatura no</TableHead>
                <TableHead className="w-40">Tip</TableHead>
                <TableHead className="hidden w-32 lg:table-cell">Lokasyon</TableHead>
                <TableHead className="w-32 text-right">Tutar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.belgeNo}>
                  <TableCell className="font-mono tnum text-ink-muted">
                    {formatDate(row.date)}
                  </TableCell>
                  <TableCell className="truncate font-mono text-ink">
                    {row.invoiceNo ?? `#${row.belgeNo}`}
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-1">
                      <Badge tone={row.side === 'debit' ? 'ok' : row.side === 'credit' ? 'warn' : 'neutral'}>
                        {row.faturaTipName}
                      </Badge>
                      {row.cancelled ? <Badge tone="neutral">İptal</Badge> : null}
                    </span>
                  </TableCell>
                  <TableCell className="hidden truncate text-micro text-ink-muted lg:table-cell">
                    {row.locationName ?? row.location ?? '—'}
                  </TableCell>
                  <TableNumCell className="font-medium">
                    {formatAmount(row.netAmount, row.currency)}
                  </TableNumCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

/* ── Small parts ──────────────────────────────────────────────────────────── */

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow">{label}</dt>
      <dd className={cn('mt-0.5 truncate text-data text-ink', mono && 'font-mono tnum')}>{value}</dd>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-0.5 font-mono text-data tnum text-ink-muted">{value}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Card>
        <div className="px-3.5 py-4">
          <span className="block h-2.5 w-24 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-6 w-64 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-2.5 w-44 animate-pulse rounded-xs bg-hairline" />
        </div>
      </Card>
      <Card>
        <TableScroller>
          <Table>
            <TableBody>
              <TableSkeleton rows={8} cols={7} />
            </TableBody>
          </Table>
        </TableScroller>
      </Card>
    </div>
  )
}
